// =============================================
// Kindergarten Map Component
// KipiKid - הצגת מפה עם מיקום הגן
// Google Maps + OpenStreetMap עם בחירת ספק
// =============================================

import { useState, useEffect, useMemo } from 'react';
import { MapPin, Navigation, ExternalLink, Map } from 'lucide-react';
import { adminColors as colors } from '../../styles/colors';

type MapProvider = 'google' | 'osm';

interface KindergartenMapProps {
  latitude?: number | null;
  longitude?: number | null;
  name: string;
  address: string;
  editable?: boolean;
  onLocationSelect?: (lat: number, lng: number) => void;
}

// Persist map provider choice in localStorage
const getStoredProvider = (): MapProvider => {
  try {
    const stored = localStorage.getItem('kipikid-map-provider');
    if (stored === 'google' || stored === 'osm') return stored;
  } catch { /* ignore */ }
  return 'google';
};

export default function KindergartenMap({
  latitude,
  longitude,
  name,
  address,
  editable = false,
  onLocationSelect,
}: KindergartenMapProps) {
  const [editLat, setEditLat] = useState(latitude?.toString() || '');
  const [editLng, setEditLng] = useState(longitude?.toString() || '');
  const [provider, setProvider] = useState<MapProvider>(getStoredProvider);
  const [iframeError, setIframeError] = useState(false);

  // Update edit fields when latitude/longitude props change
  useEffect(() => {
    setEditLat(latitude?.toString() || '');
    setEditLng(longitude?.toString() || '');
  }, [latitude, longitude]);

  const hasLocation = latitude != null && longitude != null && latitude !== 0 && longitude !== 0;
  const googleMapsKey = import.meta.env.VITE_GOOGLE_MAPS_KEY;

  const handleProviderChange = (newProvider: MapProvider) => {
    setProvider(newProvider);
    setIframeError(false);
    try {
      localStorage.setItem('kipikid-map-provider', newProvider);
    } catch { /* ignore */ }
  };

  const handleApplyCoordinates = () => {
    const lat = parseFloat(editLat);
    const lng = parseFloat(editLng);
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      onLocationSelect?.(lat, lng);
    }
  };

  // Google Maps embed URL (requires API key)
  const googleEmbedUrl = useMemo(() => {
    if (!hasLocation || !googleMapsKey) return null;
    return `https://www.google.com/maps/embed/v1/place?key=${googleMapsKey}&q=${latitude},${longitude}&zoom=16&language=he`;
  }, [hasLocation, googleMapsKey, latitude, longitude]);

  // OpenStreetMap iframe URL
  const osmEmbedUrl = useMemo(() => {
    if (!hasLocation) return null;
    const bbox = `${longitude! - 0.005},${latitude! - 0.003},${longitude! + 0.005},${latitude! + 0.003}`;
    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude},${longitude}`;
  }, [hasLocation, latitude, longitude]);

  // Google Maps link (opens in new tab - always works, no API key needed)
  const googleMapsLinkUrl = useMemo(() => {
    if (!hasLocation) return null;
    return `https://www.google.com/maps?q=${latitude},${longitude}&z=16`;
  }, [hasLocation, latitude, longitude]);

  // Determine which embed URL to use
  const embedUrl = useMemo(() => {
    if (!hasLocation) return null;
    if (provider === 'google') {
      return googleEmbedUrl || osmEmbedUrl; // fallback to OSM if no Google key
    }
    return osmEmbedUrl;
  }, [hasLocation, provider, googleEmbedUrl, osmEmbedUrl]);

  // Determine actual active provider (may differ if Google key missing)
  const activeProvider = provider === 'google' && !googleMapsKey ? 'osm' : provider;

  // Google Street View URL
  const streetViewUrl = useMemo(() => {
    if (!hasLocation) return null;
    return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${latitude},${longitude}`;
  }, [hasLocation, latitude, longitude]);

  // Google Maps navigation URL
  const navigationUrl = useMemo(() => {
    if (hasLocation) {
      return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }, [hasLocation, latitude, longitude, address]);

  const providerToggleStyle = (isActive: boolean) => ({
    padding: '4px 10px',
    borderRadius: '6px',
    border: `1px solid ${isActive ? colors.primary : colors.border}`,
    backgroundColor: isActive ? colors.primaryLight : 'white',
    color: isActive ? colors.primary : colors.textLight,
    fontSize: '11px',
    fontWeight: isActive ? '600' as const : '400' as const,
    cursor: 'pointer' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: '4px',
  });

  return (
    <div>
      {/* Header with provider toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <label style={{ fontSize: '12px', color: colors.textLight }}>
          מיקום על המפה
        </label>
        {hasLocation && (
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              type="button"
              onClick={() => handleProviderChange('google')}
              style={providerToggleStyle(activeProvider === 'google')}
            >
              <Map size={12} />
              Google
            </button>
            <button
              type="button"
              onClick={() => handleProviderChange('osm')}
              style={providerToggleStyle(activeProvider === 'osm')}
            >
              <Map size={12} />
              OpenStreetMap
            </button>
          </div>
        )}
      </div>

      {/* No Google key notice */}
      {provider === 'google' && !googleMapsKey && hasLocation && (
        <div style={{
          fontSize: '11px',
          color: colors.textLight,
          backgroundColor: '#fef3c7',
          padding: '6px 10px',
          borderRadius: '6px',
          marginBottom: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <span>Google Maps API key לא הוגדר, מוצג OpenStreetMap.</span>
          {googleMapsLinkUrl && (
            <a
              href={googleMapsLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, fontWeight: '500', textDecoration: 'none' }}
            >
              פתח ב-Google Maps
            </a>
          )}
        </div>
      )}

      {/* Map display */}
      {hasLocation && embedUrl && !iframeError ? (
        <div style={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: `1px solid ${colors.border}`,
          marginBottom: '8px',
          position: 'relative',
        }}>
          <iframe
            key={`${activeProvider}-${latitude}-${longitude}`}
            src={embedUrl}
            width="100%"
            height={editable ? 180 : 200}
            style={{ border: 0, display: 'block' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`מפה - ${name}`}
            onError={() => setIframeError(true)}
          />
        </div>
      ) : hasLocation && (iframeError || !embedUrl) ? (
        /* Fallback: link to open map externally */
        <div style={{
          height: '120px',
          borderRadius: '12px',
          backgroundColor: colors.background,
          border: `1px solid ${colors.border}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '8px',
        }}>
          <MapPin size={24} color={colors.primary} />
          <p style={{ fontSize: '13px', color: colors.text, margin: 0 }}>
            {latitude?.toFixed(4)}, {longitude?.toFixed(4)}
          </p>
          <a
            href={googleMapsLinkUrl || `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=16`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '12px',
              color: colors.primary,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <ExternalLink size={12} />
            פתח במפה חיצונית
          </a>
        </div>
      ) : !editable ? (
        <div style={{
          height: '120px',
          borderRadius: '12px',
          backgroundColor: colors.background,
          border: `1px dashed ${colors.border}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '8px',
        }}>
          <MapPin size={24} color={colors.textLight} />
          <p style={{ fontSize: '13px', color: colors.textLight }}>לא הוגדר מיקום GPS</p>
        </div>
      ) : null}

      {/* Action buttons (view mode) */}
      {hasLocation && !editable && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <a
            href={streetViewUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1,
              padding: '8px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: 'white',
              color: colors.text,
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <ExternalLink size={14} />
            Street View
          </a>
          <a
            href={navigationUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1,
              padding: '8px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: colors.primaryLight,
              color: colors.primary,
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <Navigation size={14} />
            ניווט
          </a>
        </div>
      )}

      {/* Editable coordinates */}
      {editable && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '11px', color: colors.textLight, marginBottom: '2px', display: 'block' }}>
                קו רוחב (Latitude)
              </label>
              <input
                type="number"
                step="0.000001"
                value={editLat}
                onChange={(e) => setEditLat(e.target.value)}
                placeholder="31.7683"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '13px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '11px', color: colors.textLight, marginBottom: '2px', display: 'block' }}>
                קו אורך (Longitude)
              </label>
              <input
                type="number"
                step="0.000001"
                value={editLng}
                onChange={(e) => setEditLng(e.target.value)}
                placeholder="35.2137"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '13px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleApplyCoordinates}
            disabled={!editLat || !editLng}
            style={{
              padding: '8px',
              borderRadius: '8px',
              border: `1px solid ${colors.primary}`,
              backgroundColor: 'white',
              color: colors.primary,
              fontSize: '12px',
              fontWeight: '500',
              cursor: !editLat || !editLng ? 'not-allowed' : 'pointer',
              opacity: !editLat || !editLng ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <MapPin size={14} />
            עדכן מיקום
          </button>
        </div>
      )}
    </div>
  );
}
